import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Clock, AlertTriangle, SlidersHorizontal, ListChecks, Brain, ChevronRight, Lock, MonitorSmartphone } from 'lucide-react';

const CandidateLanding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidateName, setCandidateName] = useState('');
  const [roleApplied, setRoleApplied] = useState('');
  // Fields HR filled in are shown as confirmed details, not editable inputs.
  // The server enforces this too — it ignores changes to a field HR set.
  const [nameLocked, setNameLocked] = useState(false);
  const [roleLocked, setRoleLocked] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => { loadAssessment(); }, [token]);

  const loadAssessment = async () => {
    try {
      const res = await api.getAssessmentByToken(token);
      setAssessment(res.data);
      setCandidateName(res.data.candidate_name || '');
      setRoleApplied(res.data.role_applied_for || '');
      setNameLocked(Boolean((res.data.candidate_name || '').trim()));
      setRoleLocked(Boolean((res.data.role_applied_for || '').trim()));
    } catch (err) {
      setError(err.response?.data?.detail || 'This assessment link is invalid or has expired.');
    } finally { setLoading(false); }
  };

  const handleStart = async (e) => {
    e.preventDefault();
    if (!candidateName.trim()) { toast.error('Please enter your name'); return; }
    setStarting(true);
    try {
      await api.startAssessment(token, { candidate_name: candidateName, role_applied_for: roleApplied });
      navigate(`/assess/${token}/start`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start assessment');
      setStarting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6] p-4">
      <div className="text-center bg-white p-10 rounded-none shadow-sm border border-black/5 max-w-md">
        <AlertTriangle className="h-12 w-12 text-accent2 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-[#1A231C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Assessment Unavailable</h2>
        <p className="text-[#5C665F] text-sm leading-relaxed">{error}</p>
      </div>
    </div>
  );

  const isSenior = assessment?.version === 'senior';
  const timeEstimate = isSenior ? '12-15 minutes' : '10-12 minutes';
  const qCount = assessment?.total_questions || (isSenior ? '50-55' : '40-45');

  return (
    <div className="min-h-screen bg-[#F9F9F6] flex" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Left hero panel */}
      <div className="hidden lg:flex lg:w-2/5 bg-brand relative items-end p-12">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, rgba(224,122,95,0.16), transparent 45%), radial-gradient(circle at 80% 75%, rgba(255,255,255,0.10), transparent 40%)' }} />
        <div className="relative z-10">
          <h1 className="text-3xl font-light text-white tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Wildnet's Candidate Assessment Portal
          </h1>
          <p className="text-white/50 text-sm leading-relaxed">
            An AI-powered personality assessment designed to understand your working style. There are no right or wrong answers.
          </p>
        </div>
      </div>

      {/* Right content */}
      <div className="flex-1 flex items-center justify-center p-6 py-10">
        <div className="w-full max-w-xl">
          {/* Mobile header */}
          <div className="lg:hidden mb-6">
            <h1 className="text-2xl font-medium text-[#1A231C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wildnet's Candidate Assessment Portal
            </h1>
          </div>

          {/* Welcome */}
          <p className="text-[#5C665F] text-sm leading-relaxed mb-6" data-testid="welcome-text">
            Welcome! You have been invited to complete a personality assessment for the role of <span className="font-semibold text-[#1A231C]">{assessment?.role_applied_for || 'the applied position'}</span>. Please read the guidelines below before you begin.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="quick-stats">
            <div className="p-4 bg-white border border-black/5 rounded-none">
              <Clock className="h-5 w-5 text-brand mb-2" />
              <p className="text-xs text-[#5C665F] uppercase tracking-wider">Duration</p>
              <p className="text-sm font-semibold text-[#1A231C] mt-0.5">{timeEstimate}</p>
            </div>
            <div className="p-4 bg-white border border-black/5 rounded-none">
              <ListChecks className="h-5 w-5 text-brand mb-2" />
              <p className="text-xs text-[#5C665F] uppercase tracking-wider">Questions</p>
              <p className="text-sm font-semibold text-[#1A231C] mt-0.5">{qCount}</p>
            </div>
            <div className="p-4 bg-white border border-black/5 rounded-none">
              <SlidersHorizontal className="h-5 w-5 text-brand mb-2" />
              <p className="text-xs text-[#5C665F] uppercase tracking-wider">Format</p>
              <p className="text-sm font-semibold text-[#1A231C] mt-0.5">Likert Scale</p>
            </div>
          </div>

          {/* What to Expect */}
          <div className="mb-5" data-testid="assessment-overview">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C665F] mb-3">What to Expect</h3>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 bg-white border border-black/5 rounded-none">
                <SlidersHorizontal className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#1A231C]">Likert Scale Responses</p>
                  <p className="text-xs text-[#5C665F] mt-0.5">Rate each statement on a 5-point scale: <span className="font-medium">Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree</span>.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white border border-black/5 rounded-none">
                <Brain className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#1A231C]">7 Personality Dimensions</p>
                  <p className="text-xs text-[#5C665F] mt-0.5">Accountability, Integrity, Problem Solving, Emotional Maturity, Social Empathy, Motivation & Values{isSenior ? ', and Leadership' : ''}.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white border border-black/5 rounded-none">
                <Shield className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#1A231C]">No Right or Wrong Answers</p>
                  <p className="text-xs text-[#5C665F] mt-0.5">This is not a knowledge test. We look for honest responses that reflect your natural working style.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Guidelines */}
          <div className="mb-6" data-testid="guidelines-section">
            <h3 className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C665F] mb-3">Guidelines</h3>
            <ul className="space-y-2 text-sm text-[#5C665F]">
              <li className="flex items-start gap-2.5">
                <ChevronRight className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <span>Go with your <span className="font-medium text-[#1A231C]">first instinct</span>. Don't overthink.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <ChevronRight className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <span>Complete in <span className="font-medium text-[#1A231C]">one sitting</span>. You cannot pause or go back.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <MonitorSmartphone className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <span>Use a <span className="font-medium text-[#1A231C]">laptop or desktop</span> with stable internet.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Lock className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                <span>Your responses are <span className="font-medium text-[#1A231C]">confidential</span> and monitored for integrity.</span>
              </li>
            </ul>
          </div>

          <hr className="border-black/5 mb-6" />

          {/* Form */}
          <form onSubmit={handleStart} className="space-y-4" data-testid="start-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C665F]">Your Full Name *</Label>
                {nameLocked ? (
                  <div className="border-b-2 border-black/10 px-0 py-3 text-[#1A231C] flex items-center gap-2 min-h-[50px]" data-testid="candidate-name-locked">
                    <Lock className="w-3.5 h-3.5 text-[#5C665F]/50 shrink-0" />
                    <span>{candidateName}</span>
                  </div>
                ) : (
                  <Input value={candidateName} onChange={(e) => setCandidateName(e.target.value)} required className="bg-transparent border-0 border-b-2 border-black/10 focus:border-brand focus:ring-0 rounded-none px-0 py-3 placeholder:text-black/30" data-testid="candidate-name-landing" placeholder="Enter your full name" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C665F]">Role Applied For</Label>
                {roleLocked ? (
                  <div className="border-b-2 border-black/10 px-0 py-3 text-[#1A231C] flex items-center gap-2 min-h-[50px]" data-testid="role-locked">
                    <Lock className="w-3.5 h-3.5 text-[#5C665F]/50 shrink-0" />
                    <span>{roleApplied}</span>
                  </div>
                ) : (
                  <Input value={roleApplied} onChange={(e) => setRoleApplied(e.target.value)} className="bg-transparent border-0 border-b-2 border-black/10 focus:border-brand focus:ring-0 rounded-none px-0 py-3 placeholder:text-black/30" data-testid="role-landing" placeholder="e.g. Software Engineer" />
                )}
              </div>
            </div>
            {(nameLocked || roleLocked) && (
              <p className="text-[10px] text-[#5C665F]/60">
                These details were set by the hiring team. If anything is wrong, contact them before you begin.
              </p>
            )}
            <button type="submit" disabled={starting} className="w-full bg-brand text-white hover:bg-brand-dark transition-colors rounded-none px-6 py-3.5 font-medium disabled:opacity-50" data-testid="start-assessment-button">
              {starting ? 'Starting...' : 'Begin Assessment'}
            </button>
            <p className="text-[10px] text-[#5C665F]/60 text-center">By clicking Begin Assessment, you agree to complete the assessment honestly and independently.</p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CandidateLanding;
